import { useEffect, useRef, useState } from "react";
import { getCustomisationValue, getGroupData } from "../../EditablePreview";
import VisualisedNode, { IMAGE_MODES } from "../VisualisedNode";
import './SlidePuzzle.scss';

const UNMIXED_TILE_BG_POS = [
    'top left', 'top center', 'top right',
    'center left', 'center center', 'center right',
    'bottom left', 'bottom center', 'bottom right'
];
const BLANK_SQUARE_STRINGS = [
    'topLeft', 'topCenter', 'topRight',
    'middleLeft', 'middleCenter', 'middleRight',
    'bottomLeft', 'bottomCenter', 'bottomRight'
];

const lastPuzzle = {
    index: 0,
    mixup: [],
    imgSrc: null,
    croppedPuzzleImageBlobUrl: null,
    mainContainerWidth: 1590,
    mainContainerHeight: 900,
    puzzleImageWidth: 900,
    puzzleImageHeight: 900,
    solutionImageWidth: 630,
    solutionImageHeight: 630,
}

const SlidePuzzle = (props) => {
    console.log("SlidePuzzle props:", props);
    const customisationVars = props.node.customisationVars;

    const generateLastPuzzleMixup = () => {
        const mixup = [0,1,2,3,4,5,6,7,8];
        for (let i = 0; i < mixup.length; i++) {
            const randomIndex = Math.floor(Math.random() * mixup.length);
            [mixup[i], mixup[randomIndex]] = [mixup[randomIndex], mixup[i]];
        }

        const puzzleSettings = getGroupData(customisationVars, 'setup.puzzles.puzzles', lastPuzzle.index);
        const blankSquareIndex = BLANK_SQUARE_STRINGS.indexOf(puzzleSettings.blankSquare) || 0;
        // Ensure our blank square is always in the middle
        const blankSquarePos = mixup.indexOf(blankSquareIndex);
        [mixup[4], mixup[blankSquarePos]] = [mixup[blankSquarePos], mixup[4]];
        lastPuzzle.mixup = mixup;
    };

    let puzzleIndex = lastPuzzle.index;
    if (typeof props.activeNode?.dataIndex !== 'undefined') {
        puzzleIndex = props.activeNode.dataIndex;   
    }
    if (puzzleIndex < 0) {
        puzzleIndex = 0;
    }
    if (puzzleIndex >= getGroupData(customisationVars, 'setup.puzzles.puzzles', -1).length) {
        puzzleIndex = getGroupData(customisationVars, 'setup.puzzles.puzzles', -1).length - 1;
    }
    if (puzzleIndex !== lastPuzzle.index || lastPuzzle.mixup.length === 0) {
        lastPuzzle.index = puzzleIndex;
        generateLastPuzzleMixup();
    }

    const [lastPuzzleImgSrc, setLastPuzzleImgSrc] = useState(null);
    const lastPuzzleImgSrcRef = useRef(null);
    useEffect(() => {
        lastPuzzleImgSrcRef.current = lastPuzzleImgSrc;
    }, [lastPuzzleImgSrc]);
    const puzzleSettings = getGroupData(customisationVars, 'setup.puzzles.puzzles', puzzleIndex);
    const puzzleImgSrc = getCustomisationValue(customisationVars, 'setup.puzzles.puzzles.puzzleImage', puzzleIndex, null, null).srcUrl;
    const solvedPuzzleImgSrc = puzzleSettings.solutionImageIsPuzzleImage ? puzzleImgSrc : getCustomisationValue(customisationVars, 'setup.puzzles.puzzles.solutionImage', puzzleIndex, null, null).srcUrl;
    const solutionImgSrc = getCustomisationValue(customisationVars, 'setup.puzzles.puzzles.solutionImage', puzzleIndex, null, null).srcUrl;
    const previewScale = props.previewScale || 1;

    // If our puzzle image is a rectangle we need to crop it to a square for the puzzle
    const [croppedPuzzleImageBlobUrl, setCroppedPuzzleImageBlobUrl] = useState(null);
    const createCroppedPuzzleImage = async () => {
        if (!puzzleImgSrc) return;
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = puzzleImgSrc;
        await img.decode();
        const sideLength = Math.min(img.width, img.height);
        const canvas = document.createElement('canvas');
        canvas.width = sideLength;
        canvas.height = sideLength;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, (sideLength - img.width) / 2, (sideLength - img.height) / 2);
        canvas.toBlob((blob) => {
            if (blob) {
                const url = URL.createObjectURL(blob);
                setCroppedPuzzleImageBlobUrl(url);
                lastPuzzle.croppedPuzzleImageBlobUrl = url;
            }
        });
        // Destroy image and canvas
        img.src = '';
        canvas.width = 0;
        canvas.height = 0;
        ctx.clearRect(0, 0, sideLength, sideLength);
    };

    // Pull settings from customisation data
    const visualSettings = customisationVars.hud.gameVisual.visualSettings[0];
    const gameAreaBgSettings = customisationVars.hud.gameVisual.gameAreaBgSettings[0];
    const puzzleBgSettings = customisationVars.hud.gameVisual.puzzleBgSettings[0];
    const solutionSettings = customisationVars.hud.gameVisual.solutionSettings[0];
    const tileSettings = customisationVars.hud.gameVisual.tileSettings[0];
    const downPromptSettings = customisationVars.hud.hashtagEmoteSetup.moveAboveTile[0];
    const upPromptSettings = customisationVars.hud.hashtagEmoteSetup.moveBelowTile[0];
    const leftPromptSettings = customisationVars.hud.hashtagEmoteSetup.moveRightTile[0];
    const rightPromptSettings = customisationVars.hud.hashtagEmoteSetup.moveLeftTile[0];
    
    const promptTextOutlineWidth = Math.ceil(downPromptSettings.chatStringOutlineThickness * previewScale * (visualSettings.size / 100));
    const promptTextOutlineColour = downPromptSettings.chatStringOutlineColour;
    // Use text shadows to create outline effect
    const promptTextOutlineStyle = {
        textShadow: `-${promptTextOutlineWidth}px -${promptTextOutlineWidth}px 0 ${promptTextOutlineColour}, ` +
                        `${promptTextOutlineWidth}px -${promptTextOutlineWidth}px 0 ${promptTextOutlineColour}, ` +
                        `-${promptTextOutlineWidth}px ${promptTextOutlineWidth}px 0 ${promptTextOutlineColour}, ` +
                        `${promptTextOutlineWidth}px ${promptTextOutlineWidth}px 0 ${promptTextOutlineColour}, ` +
                        `0px -${promptTextOutlineWidth}px 0 ${promptTextOutlineColour}, ` +
                        `-${promptTextOutlineWidth}px 0px 0 ${promptTextOutlineColour}, ` +
                        `0px ${promptTextOutlineWidth}px 0 ${promptTextOutlineColour}, ` +
                        `${promptTextOutlineWidth}px 0px 0 ${promptTextOutlineColour}`,
    }

    const puzzleScale = visualSettings.size / 100;

    const puzzleCarrierEdgePadding = visualSettings.addPuzzleBg ? /* Math.ceil */(puzzleBgSettings?.puzzleBgPadding * previewScale) : 0;
    const solutionCarrierEdgePadding = solutionSettings.addSolutionBg ? /* Math.ceil */(solutionSettings?.solutionBgPadding * previewScale) : 0;
    let defaultCarrierPadding = 10;
    if (puzzleCarrierEdgePadding + 5 > defaultCarrierPadding) {
        defaultCarrierPadding = puzzleCarrierEdgePadding + 5;
    }
    if (solutionCarrierEdgePadding + 5 > defaultCarrierPadding) {
        defaultCarrierPadding = solutionCarrierEdgePadding + 5;
    }
    const mainCarrierEdgePadding = visualSettings.useGameCarrier ? /* Math.ceil */(gameAreaBgSettings?.carrierPadding/*  + puzzleBgSettings?.puzzleBgPadding */ * previewScale) : defaultCarrierPadding;

    // Customise the way our renderer handles these nodes
    // props.node.boundingBoxOverflow = mainCarrierEdgePadding;
    props.node.forceProportionalScaling = true;
    props.node.subNodes[0].boundingBoxOverflow = puzzleCarrierEdgePadding;
    props.node.subNodes[1].boundingBoxOverflow = 0; // solutionCarrierEdgePadding;
    props.node.subNodes[0].dontVisualiseModuleSettings = {
        background: true,
    }
    props.node.subNodes[1].dontVisualiseModuleSettings = {
        position: true,
        dimensions: true,
        background: true,
    }
    props.node.subNodes[0].applyScale = puzzleScale;
    props.node.subNodes[1].applyScale = puzzleScale;

    let contentFlexDirection = 'row';
    switch (solutionSettings.solutionPosition) {
        case 'left':
            contentFlexDirection = 'row-reverse';
            break;
        case 'above':
            contentFlexDirection = 'column-reverse';
            break;
        case 'below':
            contentFlexDirection = 'column';
            break;
        case 'right':
        default:
            contentFlexDirection = 'row';
            break;
    }
    const contentGap = /* Math.ceil */(solutionSettings.solutionGap * previewScale * puzzleScale);
    const contentPadding = {paddingLeft: 0, paddingTop: 0, paddingRight: 0, paddingBottom: 0};
    if (visualSettings.showSolution) {
        switch (solutionSettings.solutionPosition) {
            case 'left':
                contentPadding.paddingLeft = solutionCarrierEdgePadding;
                contentPadding.paddingRight = puzzleCarrierEdgePadding;
                break;
            case 'above':
                contentPadding.paddingTop = solutionCarrierEdgePadding;
                contentPadding.paddingBottom = puzzleCarrierEdgePadding;
                break;
            case 'below':
                contentPadding.paddingTop = puzzleCarrierEdgePadding;
                contentPadding.paddingBottom = solutionCarrierEdgePadding;
                break;
            case 'right':
            default:
                contentPadding.paddingLeft = puzzleCarrierEdgePadding;
                contentPadding.paddingRight = solutionCarrierEdgePadding;
                break;
        }
    }

    const [mainContainerWidth, setMainContainerWidth] = useState(lastPuzzle.mainContainerWidth);
    const [mainContainerHeight, setMainContainerHeight] = useState(lastPuzzle.mainContainerHeight);
    const [puzzleImageWidth, setPuzzleImageWidth] = useState(lastPuzzle.puzzleImageWidth);
    const [puzzleImageHeight, setPuzzleImageHeight] = useState(lastPuzzle.puzzleImageHeight);
    const [solutionImageWidth, setSolutionImageWidth] = useState(lastPuzzle.solutionImageWidth);
    const [solutionImageHeight, setSolutionImageHeight] = useState(lastPuzzle.solutionImageHeight);

    const tileWidth = (puzzleImageWidth - ((tileSettings.tileSpacing * previewScale) * 2)) / 3;
    const tileHeight = (puzzleImageHeight - ((tileSettings.tileSpacing * previewScale) * 2)) / 3;

    const blankSquareIndex = BLANK_SQUARE_STRINGS.indexOf(puzzleSettings.blankSquare) || 0;
    let activeTileSetup = false;
    console.log("SlidePuzzle blankSquareIndex:", puzzleSettings, blankSquareIndex);

    const calculateSizes = (reset = false) => {
        const puzzleSize = /* Math.ceil */(900 * puzzleScale * previewScale);
        const solutionSize = /* Math.ceil */(puzzleSize * (solutionSettings.solutionSize / 100));

        setMainContainerWidth(visualSettings.showSolution && (solutionSettings.solutionPosition === 'left' || solutionSettings.solutionPosition === 'right') ? (puzzleSize + solutionSize + contentGap + puzzleCarrierEdgePadding + solutionCarrierEdgePadding) : puzzleSize + (puzzleCarrierEdgePadding * 2));
        setMainContainerHeight(visualSettings.showSolution && (solutionSettings.solutionPosition === 'above' || solutionSettings.solutionPosition === 'below') ? (puzzleSize + solutionSize + contentGap + puzzleCarrierEdgePadding + solutionCarrierEdgePadding) : puzzleSize + (puzzleCarrierEdgePadding * 2));
        setPuzzleImageWidth(puzzleSize);
        setPuzzleImageHeight(puzzleSize);
        setSolutionImageWidth(solutionSize);
        setSolutionImageHeight(solutionSize);
        
        lastPuzzle.mainContainerWidth = visualSettings.showSolution && (solutionSettings.solutionPosition === 'left' || solutionSettings.solutionPosition === 'right') ? (puzzleSize + solutionSize + contentGap + puzzleCarrierEdgePadding + solutionCarrierEdgePadding) : puzzleSize + (puzzleCarrierEdgePadding * 2);
        lastPuzzle.mainContainerHeight = visualSettings.showSolution && (solutionSettings.solutionPosition === 'above' || solutionSettings.solutionPosition === 'below') ? (puzzleSize + solutionSize + contentGap + puzzleCarrierEdgePadding + solutionCarrierEdgePadding) : puzzleSize + (puzzleCarrierEdgePadding * 2);
        lastPuzzle.puzzleImageWidth = puzzleSize;
        lastPuzzle.puzzleImageHeight = puzzleSize;
        lastPuzzle.solutionImageWidth = solutionSize;
        lastPuzzle.solutionImageHeight = solutionSize;

        if (lastPuzzleImgSrc !== puzzleImgSrc && lastPuzzleImgSrcRef.current !== puzzleImgSrc && lastPuzzle.imgSrc !== puzzleImgSrc) {
            console.log("SlidePuzzle puzzleImgSrc changed, recreating cropped image.", lastPuzzleImgSrc, puzzleImgSrc);
            if (croppedPuzzleImageBlobUrl) {
                URL.revokeObjectURL(croppedPuzzleImageBlobUrl);
                setCroppedPuzzleImageBlobUrl(null);
                lastPuzzle.croppedPuzzleImageBlobUrl = null;
            }
            createCroppedPuzzleImage();
            setLastPuzzleImgSrc(puzzleImgSrc);
            lastPuzzle.imgSrc = puzzleImgSrc;
        }
    }

    useEffect(() => {
        calculateSizes(true);
    }, [visualSettings.showSolution, solutionSettings.solutionPosition, solutionSettings.solutionSize, solutionSettings.solutionGap, previewScale, visualSettings.size, puzzleImgSrc, solutionImgSrc, contentGap, puzzleCarrierEdgePadding, solutionCarrierEdgePadding]);

    return (
        <div className='slide-puzzle-game-container' style={{maxWidth: `${mainContainerWidth}px`, maxHeight: `${mainContainerHeight}px`, minWidth: `${mainContainerWidth}px`, minHeight: `${mainContainerHeight}px`, ...contentPadding, margin: mainCarrierEdgePadding}}>
            {visualSettings.useGameCarrier && !gameAreaBgSettings.useCarrierImage &&
                <div 
                    style={{position: 'absolute', zIndex: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: gameAreaBgSettings.carrierBgColour, opacity: gameAreaBgSettings.carrierBgOpacity / 100, borderRadius: gameAreaBgSettings.carrierRoundness * previewScale * puzzleScale, width: `calc(100% + ${mainCarrierEdgePadding * 2}px)`, height: `calc(100% + ${mainCarrierEdgePadding * 2}px)`}} 
                />
            }
            {visualSettings.useGameCarrier && gameAreaBgSettings.useCarrierImage &&
                <div 
                    style={{position: 'absolute', zIndex: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundImage: `url(${gameAreaBgSettings.carrierFilename.srcUrl})`, backgroundSize: `cover`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: gameAreaBgSettings.carrierBgOpacity / 100, borderRadius: gameAreaBgSettings.carrierRoundness * previewScale * puzzleScale, width: `calc(100% + ${mainCarrierEdgePadding * 2}px)`, height: `calc(100% + ${mainCarrierEdgePadding * 2}px)`}} 
                />
            }
            <div className="slide-puzzle-content" style={{flexDirection: contentFlexDirection, gap: `${contentGap}px`}}>
                <div style={{width: `${puzzleImageWidth}px`, height: `${puzzleImageHeight}px`, position: 'relative'}}>
                    <VisualisedNode {...props} node={props.node.subNodes[0]}>
                        {visualSettings.addPuzzleBg && !puzzleBgSettings.useCarrierImage &&
                           <div 
                                style={{position: 'absolute', zIndex: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: puzzleBgSettings.puzzleBgColour, opacity: puzzleBgSettings.puzzleBgOpacity / 100, borderRadius: puzzleBgSettings.puzzleBgCornerRoundness * previewScale * puzzleScale, width: `calc(100% + ${puzzleCarrierEdgePadding * 2}px)`, height: `calc(100% + ${puzzleCarrierEdgePadding * 2}px)`}} 
                            />
                        }
                        {visualSettings.addPuzzleBg && puzzleBgSettings.useCarrierImage &&
                           <div 
                                style={{position: 'absolute', zIndex: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundImage: `url(${puzzleBgSettings.carrierFilename.srcUrl})`, backgroundSize: `cover`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: puzzleBgSettings.puzzleBgOpacity / 100, borderRadius: puzzleBgSettings.puzzleBgCornerRoundness * previewScale * puzzleScale, width: `calc(100% + ${puzzleCarrierEdgePadding * 2}px)`, height: `calc(100% + ${puzzleCarrierEdgePadding * 2}px)`}} 
                            />
                        }
                        <div className="slide-puzzle-puzzle-container" style={{width: `100%`, height: `${puzzleImageHeight}px`, zIndex: 2}}>
                            {props.timelinePlayheadStep >= 3 && 
                                <div style={{width: `${puzzleImageWidth}px`, height: `${puzzleImageHeight}px`, overflow: 'hidden', backgroundImage: `url(${solvedPuzzleImgSrc})`, backgroundSize: `cover`, backgroundPosition: 'center'}} />
                            }
                            {props.timelinePlayheadStep === 0 && UNMIXED_TILE_BG_POS.map((bgPos, index) => {
                                // Apply the border radius to the correct corner of the tiles
                                // const borderRadius = `${puzzleBgSettings.puzzleBgCornerRoundness * previewScale}px`;
                                /* const tileCornerStyle = {
                                    borderTopLeftRadius: (index === 0) ? borderRadius : '0px',
                                    borderTopRightRadius: (index === 2) ? borderRadius : '0px',
                                    borderBottomLeftRadius: (index === 6) ? borderRadius : '0px',
                                    borderBottomRightRadius: (index === 8) ? borderRadius : '0px',
                                }; */
                                const clickable = (!activeTileSetup && index !== blankSquareIndex);
                                /* if (clickable) {
                                    activeTileSetup = true;
                                } */
                                return (
                                    <VisualisedNode {...props} node={index === blankSquareIndex ? props.node.subNodes[0] : props.node.subNodes[0].subNodes[0]} clickable={clickable} key={index} style={{opacity: index === blankSquareIndex ? 0 : 1, borderRadius: tileSettings.tileBorderRoundness * previewScale * puzzleScale, width: `${tileWidth}px`, height: `${tileHeight}px`, overflow: 'hidden'}} dontVisualiseModuleSettings={{background: index === blankSquareIndex}}>
                                        <div style={{width: '100%', height: '100%', position: 'relative'}}>
                                            {index !== blankSquareIndex &&
                                            <>
                                                <div 
                                                    key={index}
                                                    className="slide-puzzle-tile"
                                                    style={{
                                                        width: `${tileWidth}px`,
                                                        height: `${tileHeight}px`,
                                                        backgroundPosition: bgPos,
                                                        backgroundImage: `url(${lastPuzzle?.croppedPuzzleImageBlobUrl ?? croppedPuzzleImageBlobUrl ?? puzzleImgSrc})`,
                                                        backgroundSize: `${puzzleImageWidth}px ${puzzleImageHeight}px`,
                                                        
                                                    }}
                                                />
                                                {tileSettings.addTileFg &&
                                                    <div 
                                                        style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${tileSettings.tileFgFilename?.srcUrl})`, backgroundSize: `cover`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: tileSettings.tileFgOpacity / 100}} 
                                                    />
                                                }
                                            </>
                                            }
                                        </div>
                                    </VisualisedNode>
                                );
                            })}
                            {(props.timelinePlayheadStep === 1 || props.timelinePlayheadStep === 2) && lastPuzzle.mixup.map((tileIndex, arrIndex) => {
                                // Apply the border radius to the correct corner of the tiles
                                // const borderRadius = `${puzzleBgSettings.puzzleBgCornerRoundness * previewScale}px`;
                                /* const tileCornerStyle = {
                                    borderTopLeftRadius: (index === 0) ? borderRadius : '0px',
                                    borderTopRightRadius: (index === 2) ? borderRadius : '0px',
                                    borderBottomLeftRadius: (index === 6) ? borderRadius : '0px',
                                    borderBottomRightRadius: (index === 8) ? borderRadius : '0px',
                                }; */
                                const clickable = (!activeTileSetup && tileIndex !== blankSquareIndex);
                                const bgPos = UNMIXED_TILE_BG_POS[tileIndex];
                                /* if (clickable) {
                                    activeTileSetup = true;
                                } */
                                return (
                                    <VisualisedNode {...props} node={tileIndex === blankSquareIndex ? props.node.subNodes[0] : props.node.subNodes[0].subNodes[0]} clickable={clickable} key={tileIndex} style={{opacity: tileIndex === blankSquareIndex ? 0 : 1, borderRadius: tileSettings.tileBorderRoundness * previewScale * puzzleScale, width: `${tileWidth}px`, height: `${tileHeight}px`, overflow: 'hidden'}}>
                                        <>
                                            <div 
                                                key={arrIndex}
                                                className="slide-puzzle-tile"
                                                style={{
                                                    width: `${tileWidth}px`,
                                                    height: `${tileHeight}px`,
                                                    backgroundPosition: bgPos,
                                                    backgroundImage: `url(${lastPuzzle?.croppedPuzzleImageBlobUrl ?? croppedPuzzleImageBlobUrl ?? puzzleImgSrc})`,
                                                    backgroundSize: `${puzzleImageWidth}px ${puzzleImageHeight}px`,
                                                    
                                                }}
                                            />
                                            {tileSettings.addTileFg &&
                                                <div 
                                                    style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${tileSettings.tileFgFilename?.srcUrl})`, backgroundSize: `cover`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: tileSettings.tileFgOpacity / 100}} 
                                                />
                                            }
                                        </>
                                    </VisualisedNode>
                                );
                            })}
                            
                        </div>
                        
                    </VisualisedNode>
                    {props.timelinePlayheadStep === 1 &&
                        <>
                            <div style={{position: 'absolute', zIndex: 10, top: `calc(50% - ${puzzleImageHeight / 3}px)`, left: `50%`, ...promptTextOutlineStyle}}>
                                {/* Down arrow */}
                                <div style={{position: 'relative'}}>
                                    <div style={{position: 'absolute', zIndex: 10, transform: `translate(-50%, -50%) scale(${downPromptSettings.pointerScale * previewScale * puzzleScale}%)`, transformOrigin: 'center center', top: `${(puzzleImageHeight / 3) / 2 + downPromptSettings.yOffset_pointer * previewScale * puzzleScale}px`, left: `${downPromptSettings.xOffset_pointer * previewScale * puzzleScale}px`}}>
                                        <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[0]}>
                                            <img src={getCustomisationValue(customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.directionPointer', 0, null, null).srcUrl} alt="Direction Prompt Img" />
                                        </VisualisedNode>
                                    </div>
                                    <div style={{position: 'absolute', zIndex: 11, transform: `translate(-50%, -50%)`, transformOrigin: 'center center', top: `${(puzzleImageHeight / 3) / 3 + downPromptSettings.yOffset_chatBlock * previewScale * puzzleScale}px`, left: `${downPromptSettings.xOffset_chatBlock * previewScale * puzzleScale}px`}}>
                                        <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[0]}>
                                            <div className={`slide-puzzle-prompt-text-emote-holder ${downPromptSettings.textEmoteLayout}`} style={{gap: `${downPromptSettings.chatTextEmoteSpacing * previewScale * puzzleScale}px`}}>
                                                {downPromptSettings.showText &&
                                                    <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[0].subNodes[0]} dontVisualiseModuleSettings={{border: true}} style={{whiteSpace: 'nowrap'}} />
                                                }
                                                {downPromptSettings.showEmoteImage &&
                                                    <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[0].subNodes[1]} applyScale={downPromptSettings.emoteScale / 100 * puzzleScale} imageMode={IMAGE_MODES.scaled} />
                                                }
                                            </div>
                                        </VisualisedNode>
                                    </div>
                                </div>
                            </div>
                            <div style={{position: 'absolute', zIndex: 10, top: `50%`, left: `calc(50% + ${puzzleImageHeight / 3}px)`, ...promptTextOutlineStyle}}>
                                {/* Left arrow */}
                                <div style={{position: 'relative'}}>
                                    <div style={{position: 'absolute', zIndex: 10, transform: `translate(-50%, -50%) scale(${leftPromptSettings.pointerScale * previewScale * puzzleScale}%)`, transformOrigin: 'center center', top: `${leftPromptSettings.yOffset_pointer * previewScale * puzzleScale}px`, left: `${-(puzzleImageHeight / 3) / 2 + leftPromptSettings.xOffset_pointer * previewScale * puzzleScale}px`}}>
                                        <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[1]}>
                                            <img src={getCustomisationValue(customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.directionPointer', 0, null, null).srcUrl} alt="Direction Prompt Img" />
                                        </VisualisedNode>
                                    </div>
                                    <div style={{position: 'absolute', zIndex: 11, transform: `translate(-50%, -50%)`, transformOrigin: 'center center', top: `${leftPromptSettings.yOffset_chatBlock * previewScale * puzzleScale}px`, left: `${-(puzzleImageHeight / 3) / 3 + leftPromptSettings.xOffset_chatBlock * previewScale * puzzleScale}px`}}>
                                        <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[1]}>
                                            <div className={`slide-puzzle-prompt-text-emote-holder ${leftPromptSettings.textEmoteLayout}`} style={{gap: `${leftPromptSettings.chatTextEmoteSpacing * previewScale * puzzleScale}px`}}>
                                                {leftPromptSettings.showText &&
                                                    <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[1].subNodes[0]} dontVisualiseModuleSettings={{border: true}} style={{whiteSpace: 'nowrap'}} />
                                                }
                                                {leftPromptSettings.showEmoteImage &&
                                                    <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[1].subNodes[1]} applyScale={leftPromptSettings.emoteScale / 100 * puzzleScale} imageMode={IMAGE_MODES.scaled} />
                                                }
                                            </div>
                                        </VisualisedNode>
                                    </div>
                                </div>
                            </div>
                            <div style={{position: 'absolute', zIndex: 10, top: `calc(50% + ${puzzleImageHeight / 3}px)`, left: `50%`, ...promptTextOutlineStyle}}>
                                {/* Up arrow */}
                                <div style={{position: 'relative'}}>
                                    <div style={{position: 'absolute', zIndex: 10, transform: `translate(-50%, -50%) scale(${upPromptSettings.pointerScale * previewScale * puzzleScale}%)`, transformOrigin: 'center center', top: `${-(puzzleImageHeight / 3) / 2 + upPromptSettings.yOffset_pointer * previewScale * puzzleScale}px`, left: `${upPromptSettings.xOffset_pointer * previewScale * puzzleScale}px`}}>
                                        <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[2]}>
                                            <img src={getCustomisationValue(customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.directionPointer', 0, null, null).srcUrl} alt="Direction Prompt Img" />
                                        </VisualisedNode>
                                    </div>
                                    <div style={{position: 'absolute', zIndex: 11, transform: `translate(-50%, -50%)`, transformOrigin: 'center center', top: `${-(puzzleImageHeight / 3) / 3 + upPromptSettings.yOffset_chatBlock * previewScale * puzzleScale}px`, left: `${upPromptSettings.xOffset_chatBlock * previewScale * puzzleScale}px`}}>
                                        <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[2]}>
                                            <div className={`slide-puzzle-prompt-text-emote-holder ${upPromptSettings.textEmoteLayout}`} style={{gap: `${upPromptSettings.chatTextEmoteSpacing * previewScale * puzzleScale}px`}}>
                                                {upPromptSettings.showText &&
                                                    <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[2].subNodes[0]} dontVisualiseModuleSettings={{border: true}} style={{whiteSpace: 'nowrap'}} />
                                                }
                                                {upPromptSettings.showEmoteImage &&
                                                    <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[2].subNodes[1]} applyScale={upPromptSettings.emoteScale / 100 * puzzleScale} imageMode={IMAGE_MODES.scaled} />
                                                }
                                            </div>
                                        </VisualisedNode>
                                    </div>
                                </div>
                            </div>
                            <div style={{position: 'absolute', zIndex: 10, top: `50%`, left: `calc(50% - ${puzzleImageHeight / 3}px)`, ...promptTextOutlineStyle}}>
                                {/* Right arrow */}
                                <div style={{position: 'relative'}}>
                                    <div style={{position: 'absolute', zIndex: 10, transform: `translate(-50%, -50%) scale(${rightPromptSettings.pointerScale * previewScale * puzzleScale}%)`, transformOrigin: 'center center', top: `${rightPromptSettings.yOffset_pointer * previewScale * puzzleScale}px`, left: `${(puzzleImageHeight / 3) / 2 + rightPromptSettings.xOffset_pointer * previewScale * puzzleScale}px`}}>
                                        <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[3]}>
                                            <img src={getCustomisationValue(customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.directionPointer', 0, null, null).srcUrl} alt="Direction Prompt Img" />
                                        </VisualisedNode>
                                    </div>
                                    <div style={{position: 'absolute', zIndex: 11, transform: `translate(-50%, -50%)`, transformOrigin: 'center center', top: `${rightPromptSettings.yOffset_chatBlock * previewScale * puzzleScale}px`, left: `${(puzzleImageHeight / 3) / 3 + rightPromptSettings.xOffset_chatBlock * previewScale * puzzleScale}px`}}>
                                        <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[3]}>
                                            <div className={`slide-puzzle-prompt-text-emote-holder ${rightPromptSettings.textEmoteLayout}`} style={{gap: `${rightPromptSettings.chatTextEmoteSpacing * previewScale * puzzleScale}px`}}>
                                                {rightPromptSettings.showText &&
                                                    <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[3].subNodes[0]} dontVisualiseModuleSettings={{border: true}} style={{whiteSpace: 'nowrap'}} />
                                                }
                                                {rightPromptSettings.showEmoteImage &&
                                                    <VisualisedNode {...props} node={props.node.subNodes[0].subNodes[1].subNodes[3].subNodes[1]} applyScale={rightPromptSettings.emoteScale / 100 * puzzleScale} imageMode={IMAGE_MODES.scaled} />
                                                }
                                            </div>
                                        </VisualisedNode>
                                    </div>
                                </div>
                            </div>
                        </>
                    }
                </div>
                {visualSettings.showSolution &&
                    <div className="slide-puzzle-solution-container" style={{width: `${solutionImageWidth }px`, height: `${solutionImageHeight}px`}}>
                        <div style={{zIndex: 3, position: 'absolute', top: `calc(50% + ${solutionSettings.solutionOffsetY * previewScale * puzzleScale}px)`, left: `calc(50% + ${solutionSettings.solutionOffsetX * previewScale * puzzleScale}px)`, transform: 'translate(-50%, -50%)'}}>
                            <VisualisedNode {...props} node={props.node.subNodes[1]} applyScale={puzzleScale}>
                                <div className="slide-puzzle-solution-container" style={{width: `${solutionImageWidth + solutionCarrierEdgePadding * 2}px`, height: `${solutionImageHeight + solutionCarrierEdgePadding * 2}px`}}>
                                    {solutionSettings.addSolutionBg && !solutionSettings.useCarrierImage &&
                                        <div 
                                            style={{position: 'absolute', zIndex: 3, top: `50%`, left: `50%`, transform: 'translate(-50%, -50%)', background: solutionSettings.solutionBgColour, opacity: solutionSettings.solutionBgOpacity / 100, borderRadius: solutionSettings.solutionBgCornerRoundness * previewScale * puzzleScale, width: `100%`, height: `100%`}} 
                                        />
                                    }
                                    {solutionSettings.addSolutionBg && solutionSettings.useCarrierImage &&
                                        <div 
                                            style={{position: 'absolute', zIndex: 3, top: `50%`, left: `50%`, transform: 'translate(-50%, -50%)', background: `url(${solutionSettings.carrierFilename.srcUrl})`, backgroundSize: `cover`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: solutionSettings.solutionBgOpacity / 100, borderRadius: solutionSettings.solutionBgCornerRoundness * previewScale * puzzleScale, width: `100%`, height: `100%`}} 
                                        />
                                    }
                                    <div style={{position: 'absolute', zIndex: 4, top: `50%`, left: `50%`, transform: 'translate(-50%, -50%)', width: `${solutionImageWidth}px`, height: `${solutionImageHeight}px`, overflow: 'hidden', opacity: solutionSettings.solutionOpacity / 100, filter: `saturate(${solutionSettings.solutionSaturation / 100})`}}>
                                        <div style={{width: `${solutionImageWidth}px`, height: `${solutionImageHeight}px`, backgroundImage: `url(${(solutionSettings.usePuzzleImageForSolution || puzzleSettings.solutionImageIsPuzzleImage) ? puzzleImgSrc : solutionImgSrc})`, backgroundSize: `cover`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}} />
                                    </div>
                                </div>
                            </VisualisedNode>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}

export default SlidePuzzle;
